import type { RescueDocumentOverviewData, RescueRequiredDocument } from '../../lib/documentQueries';
import { C3, documentLabel, stateName } from '../../lib/documentLabels';

const MAX_VIEWS = 3;

/** Nothing filed at all. A rejected or expiring document is the dashboard's to nudge, not this. */
export function missingDocuments(overview?: RescueDocumentOverviewData['rescueDocumentOverview']) {
  if (!overview) {
    return [];
  }
  if (!overview.documentsEnforced) {
    return [];
  }
  const ordersFreeDevices = overview.programs.some(
    (program) => program.enrolled && program.program !== 'GIFT_WITH_ADOPTION',
  );
  return ordersFreeDevices ? overview.documents.filter((entry) => entry.document === null) : [];
}

export interface MissingDocumentCopy {
  name: string;
  neededFor: string;
  detail: string;
}

/** Which free devices the rescue orders, e.g. "Fi Minis and Fi Nanos". */
export function orderedDevices(overview: RescueDocumentOverviewData['rescueDocumentOverview']) {
  const enrolled = new Set(overview.programs.filter((program) => program.enrolled).map((program) => program.program));
  const devices = [enrolled.has('FI_DEVICES') && 'Fi Minis', enrolled.has('FI_NANO') && 'Fi Nanos'].filter(Boolean);
  return devices.join(' and ');
}

export function missingDocumentCopy(
  entry: RescueRequiredDocument,
  government: boolean,
  devices: string,
): MissingDocumentCopy {
  const name = documentLabel(entry, government);
  if (entry.documentType === 'NONPROFIT_LETTER') {
    return {
      name,
      neededFor: `Needed for ${devices}`,
      detail: government
        ? 'A letter on municipal letterhead, the ordinance that set up your animal control, or a contract naming you the official agency.'
        : `The letter the IRS sent confirming your ${C3} status. Lost it? A printout from the IRS Tax Exempt Organization Search works too.`,
    };
  }
  const where = entry.state ? stateName(entry.state) : 'your state';
  const neededFor = `Needed for Fi Nanos shipped to ${where}`;
  if (entry.bucket === 'MULTISTATE_F0003') {
    return {
      name,
      neededFor,
      detail: `${where} uses the multistate form. Fill it out with Barking Labs Corp. as the seller.`,
    };
  }
  const issuer = entry.issuingAgency ? `, usually through the ${entry.issuingAgency}` : '';
  return {
    name,
    neededFor,
    detail: `The sales tax exemption certificate ${where} issued to your organization${issuer}.`,
  };
}

const viewsKey = (userId: string) => `rescueDocumentsReminderViews:${userId}`;
const LOGIN_PENDING_KEY = 'rescueDocumentsReminderLoginPending';

/** Set by both login pages, so the reminder knows this page load follows a fresh login. */
export function markReminderLogin() {
  try {
    sessionStorage.setItem(LOGIN_PENDING_KEY, '1');
  } catch {
    // Without storage the reminder never shows, which is the safe way to fail.
  }
}

/** Right after a login, for the first few. Storage can throw in private windows, so it fails closed. */
export function reminderDue(userId: string) {
  try {
    if (!sessionStorage.getItem(LOGIN_PENDING_KEY)) {
      return false;
    }
    return Number(localStorage.getItem(viewsKey(userId)) ?? 0) < MAX_VIEWS;
  } catch {
    return false;
  }
}

/** The login has been answered, whether or not the reminder had anything to show. */
export function clearReminderLogin() {
  try {
    sessionStorage.removeItem(LOGIN_PENDING_KEY);
  } catch {
    // Nothing to clear.
  }
}

export function recordReminderView(userId: string) {
  clearReminderLogin();
  try {
    localStorage.setItem(viewsKey(userId), String(Number(localStorage.getItem(viewsKey(userId)) ?? 0) + 1));
  } catch {
    // A reminder that can't count its views just stops showing; nothing to recover.
  }
}
